import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import MMAddressBook from "./MMAddressBook";
import MMCheckout from "./MMCheckout";
import "./MarsMart.css";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const MM_CART_STORAGE_KEY = "mm-cart-items";


const formatPrice = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(value) || 0);

const getStoredCart = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(MM_CART_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
};

const normalizeProduct = (product) => {
  const id = product?.id ?? product?.product_id ?? product?.sample_product_id;
  const rawStock = product?.stock ?? product?.quantity ?? product?.inventory;
  const parsedStock = Number(rawStock);
  const sellingPrice = Number(
    product?.discounted_price ??
      product?.discountedPrice ??
      product?.sale_price ??
      product?.price ??
      0
  );
  const originalPrice = Number(
    product?.original_price ??
      product?.originalPrice ??
      product?.mrp ??
      product?.price ??
      sellingPrice
  );

  return {
    id,
    name: product?.name || product?.title || "MarsMart Product",
    description:
      product?.short_description ||
      product?.description ||
      "Fresh and reliable doorstep delivery.",
    price: Number.isFinite(sellingPrice) ? sellingPrice : 0,
    originalPrice:
      Number.isFinite(originalPrice) && originalPrice >= sellingPrice
        ? originalPrice
        : Number.isFinite(sellingPrice)
        ? sellingPrice
        : 0,
    image: product?.image_url || product?.image || product?.product_image || "",
    stock:
      rawStock === undefined || rawStock === null
        ? null
        : Number.isFinite(parsedStock)
        ? parsedStock
        : null,
  };
};

export default function MarsMartStore() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => getStoredCart());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
 // ... existing states ...
const [orderHistory, setOrderHistory] = useState([]);
const [historyLoading, setHistoryLoading] = useState(false);

// Add this function inside MarsMartStore
const fetchOrderHistory = useCallback(async () => {
  if (!user?.user_id) return; // Wait until user is loaded
  setHistoryLoading(true);
  try {
    // We send the ID directly in the URL instead of a hidden token
    const res = await fetch(`${BASE_URL}/delivery/user-orders/${user.user_id}`);
    const data = await res.json();
    console.log("Fetched order history:", data);
    if (data.success) {
      setOrderHistory(data.orders || []);
    }
  } catch (err) {
    console.error("Failed to fetch history:", err);
  } finally {
    setHistoryLoading(false);
  }
}, [user]);

// Trigger fetch when modal opens
useEffect(() => {
  if (showOrders) {
    fetchOrderHistory();
  }
}, [showOrders, fetchOrderHistory]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}/delivery/products`, {
        credentials: "include",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(
          payload?.message || "Unable to load MarsMart products right now."
        );
      }

      const payload = await response.json();
      const list = Array.isArray(payload)
        ? payload
        : payload?.products || payload?.data || [];
      const normalized = list
        .map(normalizeProduct)
        .filter((item) => item.id !== undefined && item.id !== null);
      setProducts(normalized);
    } catch (err) {
      setProducts([]);
      setError(err?.message || "Something went wrong while loading products.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const cartLookup = useMemo(() => {
    const map = new Map();
    cart.forEach((item) => {
      map.set(item.id, item.quantity);
    });
    return map;
  }, [cart]);

  const cartCount = useMemo(
    () => cart.reduce((total, item) => total + item.quantity, 0),
    [cart]
  );
  const cartSubtotal = useMemo(
    () => cart.reduce((total, item) => total + item.price * item.quantity, 0),
    [cart]
  );

  useEffect(() => {
    localStorage.setItem(MM_CART_STORAGE_KEY, JSON.stringify(cart));
    window.dispatchEvent(
      new CustomEvent("mm-cart-updated", {
        detail: { count: cartCount },
      })
    );
  }, [cart, cartCount]);

  useEffect(() => {
    const handleOpenCart = () => setIsCartOpen(true);
    window.addEventListener("mm-open-cart", handleOpenCart);
    return () => window.removeEventListener("mm-open-cart", handleOpenCart);
  }, []);

  const addToCart = (product) => {
    if (product.stock === 0) return;
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        if (product.stock !== null && existing.quantity >= product.stock) {
          return prev;
        }
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const increaseQuantity = (productId) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== productId) return item;
        if (item.stock !== null && item.quantity >= item.stock) return item;
        return { ...item, quantity: item.quantity + 1 };
      })
    );
  };

  const decreaseQuantity = (productId) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

const handleOrderPlaced = (orderData) => {
  const resolvedOrderId =
    orderData?.order_id ??
    orderData?.id ??
    orderData?.order?.order_id ??
    orderData?.order?.id;

  // 1. Clear the local cart
  setCart([]);
  localStorage.removeItem(MM_CART_STORAGE_KEY);
  
  // 2. Close UI overlays
  setIsCheckoutOpen(false);
  setIsCartOpen(false);

  if (!resolvedOrderId) {
    alert("Order placed, but tracking ID was not returned by the server.");
    return;
  }

  // 3. Navigate directly to tracking page
  // The tracking page should fetch data from the DB using the ID in the URL
  navigate(`/mars-mart/track/${resolvedOrderId}`, {
    state: { orderData },
  });
};

  return (
    <div className="mm-page">
      <div className="mm-shell">
        <header className="mm-header">
          <button className="mm-back-btn" onClick={() => navigate("/mars-mart/home")}>
          <span className="mm-back-arrow">←</span> 
          <span className="mm-back-text">Back to Home</span>
        </button>
          <div>
            <p className="mm-kicker">MarsMart Demo Store</p>
            <h1 className="mm-title">Daily Essentials, Fast Checkout Flow</h1>
          </div>

          <div className="mm-header-actions">
            <button
              className="mm-refresh-btn"
              type="button"
              onClick={() => setShowOrders(true)}
            >
              My Orders
            </button>

            <button
              className="mm-refresh-btn"
              type="button"
              onClick={() => setShowAddresses(true)}
            >
              Saved Address
            </button>
          </div>
        </header>

        {showAddresses ? (
          <MMAddressBook onClose={() => setShowAddresses(false)} />
        ) : (
          <>
            {loading && <div className="mm-state-card">Loading products...</div>}
            {error && !loading && <div className="mm-state-card mm-state-error">{error}</div>}
            {!loading && !error && products.length === 0 && (
              <div className="mm-state-card">No products available yet.</div>
            )}

            {!loading && !error && products.length > 0 && (
              <section className="mm-layout">
                <div className="mm-product-grid">
                  {products.map((product) => {
                    const quantity = cartLookup.get(product.id) || 0;
                    const discountPercent =
                      product.originalPrice > product.price
                        ? Math.round(
                            ((product.originalPrice - product.price) /
                              product.originalPrice) *
                              100
                          )
                        : 0;

                    return (
                      <article key={product.id} className="mm-product-card">
                        <div className="mm-image-wrap">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="mm-product-image"
                            />
                          ) : (
                            <div className="mm-image-fallback">MarsMart</div>
                          )}
                        </div>

                        <div className="mm-card-body">
                          <h3 className="mm-product-name">{product.name}</h3>
                          <p className="mm-product-description">{product.description}</p>

                          <div className="mm-price-row">
                            <span className="mm-price">{formatPrice(product.price)}</span>
                            {product.originalPrice > product.price && (
                              <span className="mm-original-price">
                                {formatPrice(product.originalPrice)}
                              </span>
                            )}
                          </div>

                          {discountPercent > 0 && (
                            <p className="mm-discount-pill">{discountPercent}% OFF</p>
                          )}

                          <p className="mm-stock-text">
                            {product.stock === 0
                              ? "Out of stock"
                              : product.stock
                              ? `${product.stock} left`
                              : "In stock"}
                          </p>
                        </div>

                        <div className="mm-card-actions">
                          {quantity > 0 ? (
                            <div className="mm-qty-controls">
                              <button
                                type="button"
                                className="mm-qty-btn"
                                onClick={() => decreaseQuantity(product.id)}
                              >
                                -
                              </button>
                              <span className="mm-qty-value">{quantity}</span>
                              <button
                                type="button"
                                className="mm-qty-btn"
                                onClick={() => increaseQuantity(product.id)}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="mm-add-btn"
                              onClick={() => addToCart(product)}
                              disabled={product.stock === 0}
                            >
                              {product.stock === 0 ? "Sold Out" : "Add"}
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {isCartOpen && (
        <div className="mm-modal-backdrop" onClick={() => setIsCartOpen(false)}>
          <div className="mm-cart-modal" onClick={(event) => event.stopPropagation()}>
            <div className="mm-checkout-header">
              <h2 className="mm-checkout-title">Your Cart</h2>
              <button
                type="button"
                className="mm-close-btn"
                onClick={() => setIsCartOpen(false)}
              >
                X
              </button>
            </div>

            <div className="mm-cart-head">
              <span className="mm-cart-count">{cartCount} items</span>
            </div>

            {cart.length === 0 ? (
              <p className="mm-empty-cart">Add products to start your order.</p>
            ) : (
              <div className="mm-cart-list">
                {cart.map((item) => (
                  <div key={item.id} className="mm-cart-item">
                    <div>
                      <p className="mm-cart-item-name">{item.name}</p>
                      <p className="mm-cart-item-price">{formatPrice(item.price)}</p>
                      <div className="mm-inline-qty">
                        <button
                          type="button"
                          className="mm-qty-btn"
                          onClick={() => decreaseQuantity(item.id)}
                        >
                          -
                        </button>
                        <span className="mm-qty-value">{item.quantity}</span>
                        <button
                          type="button"
                          className="mm-qty-btn"
                          onClick={() => increaseQuantity(item.id)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mm-remove-btn"
                      onClick={() => removeFromCart(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mm-cart-footer">
              <p className="mm-subtotal-label">Subtotal</p>
              <p className="mm-subtotal-value">{formatPrice(cartSubtotal)}</p>
            </div>

            <button
              type="button"
              className="mm-checkout-btn"
              disabled={cart.length === 0 || !user}
              onClick={() => {
                setIsCartOpen(false);
                setIsCheckoutOpen(true);
              }}
            >
              {user ? "Proceed to Checkout" : "Login Required"}
            </button>
          </div>
        </div>
      )}

      {isCheckoutOpen && (
        <MMCheckout
          cart={cart}
          formatPrice={formatPrice}
          onClose={() => setIsCheckoutOpen(false)}
          onOrderPlaced={handleOrderPlaced}
        />
      )}

{showOrders && (
  <div className="mm-modal-backdrop" onClick={() => setShowOrders(false)}>
    <div className="mm-cart-modal" onClick={(e) => e.stopPropagation()}>
      <div className="mm-checkout-header">
        <h2 className="mm-checkout-title">Order History</h2>
        <button type="button" className="mm-close-btn" onClick={() => setShowOrders(false)}>X</button>
      </div>
      
      <div className="mm-cart-list">
        {historyLoading ? (
          <p className="mm-empty-cart">Loading your orders...</p>
        ) : orderHistory.length === 0 ? (
          <p className="mm-empty-cart">No orders found.</p>
        ) : (
          orderHistory.map((ord) => (
            <div key={ord.order_id} className="mm-cart-item">
              <div>
                <p className="mm-cart-item-name">Order #{ord.order_id}</p>
                <p className="mm-cart-item-price">
                  {new Date(ord.created_at).toLocaleDateString()}
                </p>
                <p className="mm-cart-item-price" style={{ 
                  marginTop: "4px", 
                  fontWeight: "bold",
                  color: ord.status === "DELIVERED" ? "#22c55e" : "#f59e0b" 
                }}>
                  {ord.status === "DELIVERED" ? "Delivered ✅" : `${ord.status} 🛵`}
                </p>
              </div>
              <button
                type="button"
                className="mm-add-btn"
                style={{ width: "auto", padding: "6px 12px" }}
                onClick={() => navigate(`/mars-mart/track/${ord.order_id}`)}
              >
                Track
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
)}
    </div>
  );
}
